/**
 * Remaining work to reach a set of tasks, as a fastest~longest range.
 * Scenarios that exclude any required task are ignored (that route never arrives).
 */

import { DEFAULT_STATUSES } from '../../TaskNode/constants';
import { buildChoiceSearch, missionTasksOf } from './choiceScenarios';
import { isTaskCompleteForProgress } from './taskCompletion';
import { buildWorkParts } from './workTimeRange';

function visitUpstream(startIds, excluded, ctx, parts, statuses) {
  const visited = new Set();
  const included = [];
  const taskById = new Map(ctx.tasks.map((task) => [task.id, task]));
  let min = 0;
  let max = 0;

  const walk = (id) => {
    if (visited.has(id)) return;
    visited.add(id);
    if (excluded.has(id)) return;
    const task = taskById.get(id);
    if (!task) return;
    // A finished mission is a barrier: its prerequisites are not remaining work.
    // An answered decision is also "complete", but it is only a choice — keep
    // walking so missions before the choice still count.
    if (task.nodeType !== 'decision' && isTaskCompleteForProgress(task, statuses)) return;
    for (const dep of ctx.depsByTask.get(id) || []) {
      walk(dep.taskId);
    }
    const part = parts.get(id);
    if (!part || part.skip) return;
    min += part.min;
    max += part.max;
    included.push(id);
  };

  for (const id of startIds) walk(id);
  return { min, max, included };
}

/**
 * @returns {{ minMinutes: number, maxMinutes: number, totalMinutes: number, missingEstCount: number, incompleteCount: number }}
 */
export function calculateUpstreamWorkTimeRange(
  tasks,
  startIds,
  {
    statuses = DEFAULT_STATUSES,
    nestedSprints = [],
    allSprints = [],
    requiredTaskIds = [],
  } = {},
) {
  const empty = {
    minMinutes: 0,
    maxMinutes: 0,
    totalMinutes: 0,
    missingEstCount: 0,
    incompleteCount: 0,
  };
  const missionTasks = missionTasksOf(tasks);
  const roots = (startIds || []).filter(Boolean);
  if (missionTasks.length === 0 || roots.length === 0) return empty;

  const parts = buildWorkParts(missionTasks, {
    statuses,
    nestedSprints,
    allSprints,
    remainingOnly: true,
    pathPrefix: [],
    seenSprintIds: new Set(),
  });
  const search = buildChoiceSearch(missionTasks);
  const assignments = search.listAssignments(parts);

  let minMinutes = Infinity;
  let maxMinutes = -Infinity;
  const union = new Set();
  let any = false;

  for (const assignment of assignments) {
    const excluded = search.exclude(assignment);
    if (requiredTaskIds.some((id) => excluded.has(id))) continue;
    const visited = visitUpstream(roots, excluded, search.ctx, parts, statuses);
    any = true;
    for (const id of visited.included) union.add(id);
    if (visited.min < minMinutes) minMinutes = visited.min;
    if (visited.max > maxMinutes) maxMinutes = visited.max;
  }

  if (!any || !Number.isFinite(minMinutes)) return empty;

  let missingEstCount = 0;
  let incompleteCount = 0;
  for (const id of union) {
    const part = parts.get(id);
    missingEstCount += part?.missing || 0;
    incompleteCount += part?.incomplete || 0;
  }

  return {
    minMinutes,
    maxMinutes,
    totalMinutes: maxMinutes,
    missingEstCount,
    incompleteCount,
  };
}
